import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { AppShell } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormField';
import { LabelChipGrid } from '@/components/ui/LabelChipGrid';
import { ReaderAvatar } from '@/components/ui/ReaderAvatar';
import { ReaderAvatarPicker } from '@/components/readers/ReaderAvatarPicker';
import { SectionTitle } from '@/components/ui/SectionTitle';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { InlineLoader } from '@/components/ui/InlineLoader';
import { readersService } from '@/services/readers.service';
import { labelsService } from '@/services/labels.service';
import { useReadersStore } from '@/store/readers.store';
import { ApiError } from '@/services/http';
import {
  DEFAULT_READER_AVATAR_SLUG,
  readerAvatarOptionForSlug,
  type ReaderAvatarSlug,
} from '@/constants/reader-avatars';
import type { LabelView } from '@/types/api';

/**
 * ReaderEdit — `/readers/:id`. Owns the full detail view of a reader:
 * name edit, reading-level multi-pick (readers often span more than one
 * level), and the destructive "Remove reader" button.
 *
 * Genre/topic selection was removed to simplify reader setup — reading level
 * is the only interest a parent picks. Any genre/topic interests a reader
 * already had are preserved (carried through the save payload untouched), so
 * editing reading level never silently wipes pre-existing interests.
 *
 * We hydrate from the readers store synchronously so there's no empty flash,
 * then refresh the labels from `GET /readers/:id/labels` to catch any edits
 * made in another tab. Labels are persisted with a single PUT that replaces
 * the reader's full label set — the server treats `labelIds` as the complete
 * new set, so we compose it from the preserved interest selection and every
 * chosen reading level on submit.
 */
export function ReaderEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const readers = useReadersStore((s) => s.readers);
  const upsertReader = useReadersStore((s) => s.upsertReader);
  const removeReader = useReadersStore((s) => s.removeReader);

  const reader = useMemo(() => readers.find((r) => r.id === id) ?? null, [readers, id]);

  const [name, setName] = useState(reader?.name ?? '');
  const [avatarSlug, setAvatarSlug] = useState<ReaderAvatarSlug>(
    readerAvatarOptionForSlug(reader?.avatarSlug ?? DEFAULT_READER_AVATAR_SLUG).slug,
  );
  const [savingName, setSavingName] = useState(false);
  const [savingAvatar, setSavingAvatar] = useState(false);

  const [readingLevels, setReadingLevels] = useState<LabelView[]>([]);
  const [labelsLoaded, setLabelsLoaded] = useState(false);

  // Holds the reader's existing genre/topic/interest labels. No longer
  // user-editable (the pickers were removed), but kept so the save payload
  // preserves them instead of wiping them when reading level changes.
  const [selectedLabelIds, setSelectedLabelIds] = useState<Set<string>>(new Set());
  // Reading levels are multi-select; a reader can legitimately sit between
  // levels (picture book for bedtime, early chapter book during the day).
  const [selectedReadingLevelIds, setSelectedReadingLevelIds] = useState<Set<string>>(new Set());
  const [savingLabels, setSavingLabels] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Seed the selection sets from the cached reader's labels.
  useEffect(() => {
    if (!reader) return;
    setName(reader.name);
    setAvatarSlug(readerAvatarOptionForSlug(reader.avatarSlug).slug);
    // interests bucket contains genre + topic + interest_level labels (the
    // server RPC puts everything that isn't a reading_level in here).
    setSelectedLabelIds(new Set(reader.interests.map((l) => l.labelId)));
    setSelectedReadingLevelIds(new Set(reader.readingLevels.map((l) => l.labelId)));
  }, [reader]);

  // Load the reading-level catalogue once for the chip grid.
  useEffect(() => {
    if (labelsLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const levels = await labelsService.list('reading_level');
        if (cancelled) return;
        setReadingLevels(levels);
        setLabelsLoaded(true);
      } catch (err) {
        if (cancelled) return;
        toast.error(err instanceof ApiError ? err.message : 'Failed to load reading levels.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [labelsLoaded]);

  if (!reader) {
    return (
      <AppShell mode="kid">
        <div className="rounded-2xl border border-or-slate-200 bg-white p-8 text-center">
          <p className="font-display text-[18px] font-bold text-or-slate-900">
            That reader doesn't exist
          </p>
          <p className="mt-1 text-[13px] text-or-slate-600">
            They may have been removed from another tab.
          </p>
          <Link
            to="/readers"
            className="mt-4 inline-flex items-center gap-1 text-[13px] font-semibold text-or-orange-600"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
            Back to readers
          </Link>
        </div>
      </AppShell>
    );
  }

  const currentAvatarSlug = readerAvatarOptionForSlug(reader.avatarSlug).slug;

  const saveName = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error('Name cannot be empty.');
      return;
    }
    if (trimmed === reader.name) return;

    setSavingName(true);
    try {
      const updated = await readersService.update(reader.id, { name: trimmed });
      upsertReader(updated);
      toast.success('Name saved.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save the name.');
    } finally {
      setSavingName(false);
    }
  };

  const saveAvatar = async () => {
    if (avatarSlug === currentAvatarSlug) return;

    setSavingAvatar(true);
    try {
      const updated = await readersService.update(reader.id, { avatarSlug });
      upsertReader(updated);
      toast.success('Avatar saved.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save the avatar.');
    } finally {
      setSavingAvatar(false);
    }
  };

  const saveLabels = async () => {
    // Compose the full label set: the reader's preserved (non-editable)
    // genre/topic interests plus the currently chosen reading levels.
    const labelIds = [...selectedLabelIds, ...selectedReadingLevelIds];

    setSavingLabels(true);
    try {
      await readersService.setLabels(reader.id, { labelIds });
      // Refetch the reader so the interests/readingLevels buckets match the
      // canonical server split (bookshelf RPC re-groups by labelType).
      const list = await readersService.list();
      const fresh = list.find((r) => r.id === reader.id);
      if (fresh) upsertReader(fresh);
      toast.success('Reading level updated.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save reading level.');
    } finally {
      setSavingLabels(false);
    }
  };

  const deleteReader = async () => {
    setDeleting(true);
    try {
      await readersService.remove(reader.id);
      removeReader(reader.id);
      toast.success(`${reader.name} has been removed.`);
      navigate('/readers', { replace: true });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not remove the reader.');
      setDeleting(false);
      setConfirmOpen(false);
    }
  };

  const toggleReadingLevel = (labelId: string) =>
    setSelectedReadingLevelIds((prev) => {
      const next = new Set(prev);
      if (next.has(labelId)) next.delete(labelId);
      else next.add(labelId);
      return next;
    });

  return (
    <AppShell mode="kid">
      <div className="flex flex-col gap-6">
        <Link
          to="/readers"
          className="inline-flex items-center gap-1 text-[13px] font-semibold text-or-slate-600 hover:text-or-orange-700"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Back to readers
        </Link>

        <div className="flex items-center gap-4">
          <ReaderAvatar name={reader.name} avatarSlug={reader.avatarSlug} size="xl" />
          <div>
            <div className="or-kicker">Reader profile</div>
            <h1 className="font-display text-[26px] font-bold text-or-slate-900">{reader.name}</h1>
          </div>
        </div>

        <section className="rounded-2xl border border-or-slate-200 bg-white p-6">
          <SectionTitle kicker="Profile" heading="Name" />
          <form onSubmit={saveName} className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-end">
            <div className="flex-1">
              <FormField
                label="Display name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
              />
            </div>
            <Button type="submit" size="md" disabled={savingName || name.trim() === reader.name}>
              {savingName ? 'Saving…' : 'Save name'}
            </Button>
          </form>
        </section>

        <section className="rounded-2xl border border-or-slate-200 bg-white p-6">
          <SectionTitle
            kicker="Profile"
            heading="Avatar"
            action={
              <Button
                size="md"
                onClick={saveAvatar}
                disabled={savingAvatar || avatarSlug === currentAvatarSlug}
              >
                {savingAvatar ? 'Saving…' : 'Save avatar'}
              </Button>
            }
          />
          <div className="mt-4">
            <ReaderAvatarPicker
              value={avatarSlug}
              onChange={setAvatarSlug}
              name={name || reader.name}
              disabled={savingAvatar}
            />
          </div>
        </section>

        <section className="rounded-2xl border border-or-slate-200 bg-white p-6">
          <SectionTitle
            kicker="Reading level"
            heading="Reading level"
            action={
              <Button size="md" onClick={saveLabels} disabled={savingLabels}>
                {savingLabels ? 'Saving…' : 'Save reading level'}
              </Button>
            }
          />
          <div className="mt-5">
            {!labelsLoaded ? (
              <InlineLoader label="Loading reading levels…" />
            ) : (
              <div>
                <div className="mb-2 text-[12px] text-or-slate-500">
                  Pick any that fit — readers often span a range.
                </div>
                <LabelChipGrid
                  labels={readingLevels}
                  selectedIds={selectedReadingLevelIds}
                  onToggle={toggleReadingLevel}
                  emptyLabel="No reading levels configured yet."
                />
              </div>
            )}
          </div>
        </section>

        <section className="rounded-2xl border border-or-rose-100 bg-or-rose-100/30 p-6">
          <div className="font-display text-[16px] font-bold text-or-rose-700">Danger zone</div>
          <p className="mt-1 text-[13px] text-or-slate-700">
            Removing a reader deletes their bookmarks and interests — this can't be undone.
          </p>
          <Button variant="danger" className="mt-4" onClick={() => setConfirmOpen(true)}>
            <Trash2 className="h-4 w-4" aria-hidden />
            Remove reader
          </Button>
        </section>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={`Remove ${reader.name}?`}
        description="This deletes the reader, their bookmarks, and their interests. You can't undo this."
        confirmLabel="Remove reader"
        onConfirm={deleteReader}
        isConfirming={deleting}
      />
    </AppShell>
  );
}
